<template>
    <v-container fluid class="pa-8">
        <!-- Header -->
        <div class="d-flex justify-space-between align-center mb-6">
            <div>
            <h1 class="text-h5 font-weight-bold colorText">
                Dashboard Ejecutivo de Conectividad
            </h1>
            <p class="text-caption colorText mb-0 ">
                Monitoreo unificado de infraestructura LTE
            </p>
            </div>
            <div>
            <span class="text-caption colorText ">
                Última actualización: Hace unos segundos
            </span>
            </div>
        </div>

        <!-- KPI Grid -->
        <v-row class="mb-4">
            <!-- KPI 1 -->
            <v-col cols="12" sm="6" md="3">
            <v-card class="pa-4 border-slate-700" variant="outlined" rounded="lg">
                <div class="text-overline font-weight-bold line-height-tight">
                <span class="colorText" color="white"> Disponibilidad de Flota </span>
                </div>
                <div class="text-h4 font-weight-bold my-2 colorText">75%</div>
                <v-progress-linear
                model-value="75"
                color="blue-accent-3"
                height="6"
                rounded
                class="mb-3"
                />
                <div class="text-caption colorText d-flex align-center gap-2">
                <span class="text-success font-weight-bold">● 6 Online</span>
                <span class="mx-1">|</span>
                <span class="text-error font-weight-bold">● 2 Offline</span>
                </div>
            </v-card>
            </v-col>

            <!-- KPI 2 -->
            <v-col cols="12" sm="6" md="3">
            <v-card  class="pa-4 border-slate-700" variant="outlined" rounded="lg">
                <div class="text-overline colorText font-weight-bold line-height-tight">
                Salud de Red (SINR Promedio)
                </div>
                <div class="text-h4 font-weight-bold my-2 colorText">13 dB</div>
                <div class="text-caption text-success font-weight-medium mt-7">
                ▲ Óptimo (>10dB)
                </div>
            </v-card>
            </v-col>

            <!-- KPI 3 -->
            <v-col cols="12" sm="6" md="3">
            <v-card  class="pa-4 border-slate-700" variant="outlined" rounded="lg">
                <div class="text-overline colorText font-weight-bold line-height-tight">
                Estabilidad (Uptime Promedio)
                </div>
                <div class="text-h4 font-weight-bold my-2 colorText">5h 42m</div>
                <div class="text-caption text-warning font-weight-medium mt-7">
                ▼ Caídas detectadas recientemente
                </div>
            </v-card>
            </v-col>

            <!-- KPI 4 -->
            <v-col cols="12" sm="6" md="3">
            <v-card  class="pa-4 border-slate-700" variant="outlined" rounded="lg">
                <div class="text-overline colorText font-weight-bold line-height-tight">
                Alertas Críticas
                </div>
                <div class="text-h4 font-weight-bold my-2 text-error">2</div>
                <div class="text-caption colorText font-weight-medium mt-7">
                Equipos buscando red
                </div>
            </v-card>
            </v-col>
        </v-row>

        <!-- Dashboard Main Panels -->
        <v-row>
            <!-- Left Table Panel -->
            <v-col cols="12" lg="8">
            <v-card  class="pa-5 border-slate-700" variant="outlined" rounded="lg">
                <h2 class="text-subtitle-1 font-weight-bold colorText mb-4">
                Estado de Equipos Críticos
                </h2>

                <v-data-table
                :headers="headers"
                :items="devices"
                density="comfortable"
                class="bg-transparent border-0 custom-table"
                hide-default-footer
                >
                <!-- Custom status badge rendering -->
                <template #item.status="{ item }">
                    <v-chip
                    :color="item.status === 'Conectado' ? 'success' : 'error'"
                    size="small"
                    variant="tonal"
                    class="font-weight-bold"
                    >
                    {{ item.status }}
                    </v-chip>
                </template>

                <!-- Signal Quality rendering -->
                <template #item.signal="{ item }">
                    <span :class="getSignalClass(item.signal)">
                    {{ item.signal }}
                    </span>
                </template>

                <!-- Action rendering -->
                <template #item.action="{ item }">
                    <span
                    :class="{ 'text-error font-weight-bold': item.action === 'Revisar Sitio' }"
                    >
                    {{ item.action }}
                    </span>
                </template>
                </v-data-table>
            </v-card>
            </v-col>

            <!-- Right Side Load Panel -->
            <v-col cols="12" lg="4">
            <v-card  class="pa-5 border-slate-700" variant="outlined" rounded="lg">
                <h2 class="text-subtitle-1 font-weight-bold colorText mb-4">
                Carga por Estación Base
                </h2>

                <div
                v-for="(cell, index) in cells"
                :key="cell.id"
                class="d-flex justify-space-between align-center py-3"
                :class="{ 'border-b-thin border-slate-700': index !== cells.length - 1 }"
                >
                <div>
                    <h4 class="text-body-2 font-weight-bold colorText mb-0">
                    Celda ID: {{ cell.id }}
                    </h4>
                    <p class="text-caption colorText mb-0">
                    {{ cell.details }}
                    </p>
                </div>
                <div
                    class="cell-badge text-body-1 font-weight-bold px-3 py-1 rounded-lg"
                    :class="cell.colorClass"
                >
                    {{ cell.count }}
                </div>
                </div>

                <!-- Extra Note -->
                <div class="mt-4 pt-4 border-t-dashed border-slate-700">
                <p class="text-caption colorText mb-0">
                    La Celda 4 concentra el 66% del tráfico actual. Se sugiere balanceo si se agregan más equipos.
                </p>
                </div>
            </v-card>
            </v-col>
        </v-row>
        </v-container>
</template>

<script setup lang="ts">
import { ref } from 'vue'

// Data Table Interfaces & Definitions
interface Device {
  name: string
  status: 'Conectado' | 'Buscando'
  signal: 'Excelente' | 'Aceptable' | 'Sin Señal'
  phyCell: string | number
  action: string
}

interface CellLoad {
  id: number
  details: string
  count: number
  colorClass: string
}

const headers = [
  { title: 'EQUIPO', key: 'name', sortable: false },
  { title: 'ESTADO', key: 'status', sortable: false },
  { title: 'CALIDAD SEÑAL', key: 'signal', sortable: false },
  { title: 'CELDA (PHY)', key: 'phyCell', sortable: false },
  { title: 'ACCIÓN', key: 'action', sortable: false }
]

const devices = ref<Device[]>([
  { name: 'GW02-HG5-24009', status: 'Conectado', signal: 'Excelente', phyCell: 4, action: '-' },
  { name: 'DT669-HG5-24021', status: 'Conectado', signal: 'Excelente', phyCell: 4, action: '-' },
  { name: 'ES030', status: 'Conectado', signal: 'Aceptable', phyCell: 1, action: 'Monitorear' },
  { name: 'DT682-HG5-24012', status: 'Buscando', signal: 'Sin Señal', phyCell: '-', action: 'Revisar Sitio' },
  { name: 'DT668-HG5-24019', status: 'Buscando', signal: 'Sin Señal', phyCell: '-', action: 'Revisar Sitio' }
])

const cells = ref<CellLoad[]>([
  { id: 4, details: 'B28@10Mhz (earfcn: 9260)', count: 4, colorClass: 'text-warning bg-slate-800' },
  { id: 1, details: 'B28@10Mhz (earfcn: 9260)', count: 2, colorClass: 'text-success bg-slate-800' }
])

// Helper Methods
const getSignalClass = (signal: Device['signal']) => {
  switch (signal) {
    case 'Excelente':
      return 'text-slate-100'
    case 'Aceptable':
      return 'text-warning font-weight-medium'
    case 'Sin Señal':
      return 'text-error font-weight-medium'
    default:
      return 'text-slate-100'
  }
}
</script>

<style scoped>
.bg-slate-900 {
  background-color: #0f172a !important;
}

.border-slate-700 {
  border-color: #334155 !important;
}

.bg-slate-800 {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

.border-t-dashed {
  border-top: 1px dashed #334155 !important;
}

.border-e-thin {
  border-right: 1px solid #334155 !important;
}

.custom-table :deep(th) {
  color: #94a3b8 !important;
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
}

.custom-table :deep(td) {
  border-bottom: 1px solid #334155 !important;
}

.line-height-tight {
  line-height: 1.25rem;
}
</style>